import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import { SortTypes } from "../../../../core/Enums";
import { FontAwesomeIcons } from "../../../../core/Types";
import { useScreen } from "../../../../core/Global";
import DataItem from "../../../../models/DataItem";
import { LIST_COLUMN_GAP, useListLayout } from "../utils/jobsBoardSettings";



interface IProps {
    group: DataItem
    showTitles?: boolean
    selectable?: boolean
    /** This group's current column sort, if any. */
    sort?: { sortKey?: string, sortType?: SortTypes }
    onSort: (key: string) => void
    /** Grid view has no column titles, so the header offers a sort menu instead. */
    sortMenu?: boolean
    onSetSort?: (key?: string, sortType?: SortTypes) => void
}

// What a group can be sorted by from the grid view's menu (keys match the board's jobsSort).
const SORT_OPTIONS: { key: string, title: string, icon: FontAwesomeIcons }[] = [
    { key: "name", title: "Customer Name", icon: "user" },
    { key: "type", title: "Job Type", icon: "wrench" },
    { key: "source", title: "Job Source", icon: "bullhorn" },
    { key: "address", title: "Address", icon: "location-dot" },
    { key: "metro", title: "Metro", icon: "map" },
    { key: "tech", title: "Technician", icon: "user-helmet-safety" },
    { key: "appointment", title: "Appointment", icon: "calendar-clock" },
    { key: "localTime", title: "Local Time", icon: "clock" },
    { key: "modified", title: "Last Modified", icon: "clock-rotate-left" },
    { key: "created", title: "Created", icon: "calendar-plus" },
    { key: "statusChange", title: "Status Changed", icon: "arrows-rotate" },
]

export default function JobDataFullCardHead(props: IProps) {

    const screen = useScreen();
    const layout = useListLayout(screen.width);

    // The sort menu is driven by React state, not the theme's data-kt-menu: it must close on a
    // pick and on any click outside it, and the theme's menu (kept open by DropdownMenu's
    // "permanent" flag, re-created when the header re-renders) did neither.
    // It is portaled to <body> with fixed positioning: inside the header it shared the sticky
    // header's stacking layer, so the next group's sticky header painted over it.
    const [menuOpen, menuOpenSet] = useState<boolean>(false)
    const [menuPos, menuPosSet] = useState<{ top: number, right: number }>()
    const menuRef = useRef<HTMLDivElement>(null)
    const popupRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!menuOpen) return
        const rect = menuRef.current?.getBoundingClientRect()
        if (rect) menuPosSet({ top: rect.bottom + 6, right: window.innerWidth - rect.right })

        const close = (e: MouseEvent) => {
            const t = e.target as Node
            if (menuRef.current?.contains(t) || popupRef.current?.contains(t)) return
            menuOpenSet(false)
        }
        const esc = (e: KeyboardEvent) => { if (e.key == "Escape") menuOpenSet(false) }
        // The button moves away when the board scrolls, so close rather than follow it.
        // Capture catches the board's own scroller, whose scroll doesn't bubble.
        const hide = (e: Event) => { if (!popupRef.current?.contains(e.target as Node)) menuOpenSet(false) }
        document.addEventListener("mousedown", close)
        document.addEventListener("keydown", esc)
        window.addEventListener("scroll", hide, true)
        window.addEventListener("resize", hide)
        return () => {
            document.removeEventListener("mousedown", close)
            document.removeEventListener("keydown", esc)
            window.removeEventListener("scroll", hide, true)
            window.removeEventListener("resize", hide)
        }
    }, [menuOpen])

    // Column titles follow the person's Customize Board columns and their smart widths. The
    // group title spans the job id column and the first chosen column, so the titles start at
    // the second one.
    const columns = layout.columns
    const titleWidth = layout.idWidth + (columns[0] ? layout.width(columns[0].id) : 0)

    // Arrow next to the sorted column's title: up = ascending, down = descending, none = unsorted.
    const sortIcon = (key: string) =>
        props.sort?.sortKey == key
            ? <Icon icon={props.sort.sortType == SortTypes.Descending ? "arrow-down-wide-short" : "arrow-up-short-wide"}
                type="solid" size={11} className="ms-1 text-primary" />
            : <></>

    const activeSort = SORT_OPTIONS.find(o => o.key == props.sort?.sortKey)
    const descending = props.sort?.sortType == SortTypes.Descending


    const pickSort = (key?: string) => {
        menuOpenSet(false)
        if (!key) props.onSetSort?.(undefined)
        // Picking the active field again keeps its direction; a new field starts ascending.
        else props.onSetSort?.(key, key == props.sort?.sortKey ? props.sort?.sortType : SortTypes.Ascending)
    }

    const sortMenuItem = (key: string | undefined, title: string, icon: FontAwesomeIcons) => {
        const selected = (activeSort?.key ?? undefined) == key
        return (
            <a key={key ?? "none"} href="#" className={`jobs-grid-sort-item${selected ? " selected" : ""}`}
                onClick={e => {
                    e.preventDefault()
                    pickSort(key)
                }}>
                <Icon icon={icon} type="solid" size={12} className="me-2" />
                {title}
                {selected && <Icon icon="check" size={11} className="ms-auto" />}
            </a>
        )
    }

    return (
        <div className="job-data-full-card-head bg-light pt-2 pb-2"
            style={{
                borderBottom: "5px solid " + (props.group.color ?? "#CCC"),
                // Matches the rows' left inset (the job-colour band's lane) so the column
                // titles line up with the column contents.
                paddingLeft: 14,
                // As wide as the rows when their columns overflow the board (sideways scroll).
                minWidth: props.showTitles ? layout.total + 14 + 24 + 2 : undefined,
            }}>
            <FlexRow className="w-100">

                {/* The rows' checkbox lane (24px) is folded into the title column, so the group
                    title sits further left while the column titles stay over their contents. */}
                <div style={{
                    width: props.showTitles ? titleWidth + (props.selectable ? 24 : 0) : undefined,
                    flex: props.showTitles ? undefined : 1,
                    // Fixed like the row's cells, or a crowded header squeezes it and every column
                    // title slides left of its column.
                    flexShrink: props.showTitles ? 0 : undefined,
                    minWidth: 0,
                    overflow: "hidden"
                }}>
                    <FlexRow className="job-dashboard-status-title">
                        <Icon icon={props.group.icon} color={props.group.color} type="solid" className="me-2" />
                        <span className="limited-text"
                            style={{
                                width: props.showTitles ? titleWidth - 50 : "100%",
                            }}>
                            {props.group.name}
                            <span className="fs-5 ms-2">
                                ({props.group.count})
                            </span>
                        </span>

                    </FlexRow>
                </div>

                {
                    props.sortMenu &&
                    <FlexRow className="jobs-grid-sort me-2">
                        {
                            activeSort &&
                            <a href="#" className="jobs-grid-sort-dir me-1"
                                title={descending ? "Descending — click for ascending" : "Ascending — click for descending"}
                                onClick={e => {
                                    e.preventDefault()
                                    props.onSetSort?.(activeSort.key, descending ? SortTypes.Ascending : SortTypes.Descending)
                                }}>
                                <Icon icon={descending ? "arrow-down-wide-short" : "arrow-up-short-wide"} type="solid" size={13} />
                            </a>
                        }
                        <div ref={menuRef}>
                            <a href="#" className={`jobs-grid-sort-btn${activeSort || menuOpen ? " active" : ""}`}
                                onClick={e => {
                                    e.preventDefault()
                                    menuOpenSet(o => !o)
                                }}>
                                <Icon icon="arrow-down-arrow-up" size={12} className="me-2" />
                                {activeSort ? activeSort.title : "Sort"}
                                <Icon icon={menuOpen ? "chevron-up" : "chevron-down"} size={9} className="ms-2" />
                            </a>
                            {
                                menuOpen && menuPos && createPortal(
                                    <div ref={popupRef} className="jobs-grid-sort-menu"
                                        style={{ top: menuPos.top, right: menuPos.right }}>
                                        {sortMenuItem(undefined, "Default order", "bars-staggered")}
                                        <div className="jobs-grid-sort-sep" />
                                        {SORT_OPTIONS.map(o => sortMenuItem(o.key, o.title, o.icon))}
                                    </div>,
                                    document.body)
                            }
                        </div>
                    </FlexRow>
                }

                {
                    props.showTitles &&
                    columns.slice(1).map(c => (
                        <div key={`col-title-${c.id}`} className="fs-7 text-muted" style={{
                            width: layout.width(c.id),
                            minWidth: layout.width(c.id),
                            maxWidth: layout.width(c.id),
                            paddingRight: LIST_COLUMN_GAP,
                            textOverflow: "ellipsis",
                            flexShrink: 0,
                            textTransform: "uppercase",
                            fontWeight: "bold",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            cursor: c.sortKey ? "pointer" : undefined
                        }}
                            onClick={e => {
                                e.preventDefault()
                                if (c.sortKey) props.onSort(c.sortKey)
                            }}>
                            {c.title}
                            {c.sortKey && sortIcon(c.sortKey)}
                        </div>
                    ))
                }


            </FlexRow>
        </div>
    )
}